import { Button, ButtonVariant, Container, ContainerHeader } from '@Core/Components'
import { BackButton } from '@Core/Components/BackButton/BackButton'
import { Loader } from '@Core/Components/Loader/Loader'
import { PermissionWrapper } from '@Core/Components/PermissionWrapper/PermissionWrapper'
import { usePermissions } from '@Core/Hooks/usePermissions'
import { usePurchaseProcessAudit } from '@Purchasing/Hooks/usePurchaseProcessAudit/usePurchaseProcessAudit'
import { usePurchaseProcessById } from '@Purchasing/Hooks/usePurchaseProcessById/usePurchaseProcessById'
import { usePurchaseProcessManager } from '@Purchasing/Hooks/usePurchaseProcessManager/usePurchaseProcessManager'
import { usePurchasingStages } from '@Purchasing/Hooks/usePurchasingStages/usePurchasingStages'
import { useSupplierQuotes } from '@Purchasing/Hooks/useSupplierQuotes/useSupplierQuotes'
import { usePurchase } from '@Purchasing/Hooks/usePurchase/usePurchase'
import { usePurchaseActions } from '@Purchasing/Hooks/usePurchaseActions/usePurchaseActions'
import { usePurchaseFinance } from '@Purchasing/Hooks/usePurchaseFinance/usePurchaseFinance'
import { usePurchasePaymentActions } from '@Purchasing/Hooks/usePurchasePaymentActions/usePurchasePaymentActions'
import { useProductionLinks } from '@Purchasing/Hooks/useProductionLinks/useProductionLinks'
import { ProductionSection } from './production.parts'
import {
    FinanceSummaryCard,
    MarkPaidModal,
    NewPaymentModal,
    PaymentsTable,
} from './finance.parts'
import type { PurchasePayment, PurchasingAttachment } from '@Purchasing/Domain/Model/PurchaseProcess'
import { useProcessAttachments } from '@Purchasing/Hooks/useProcessAttachments/useProcessAttachments'
import { useAttachmentActions } from '@Purchasing/Hooks/useAttachmentActions/useAttachmentActions'
import {
    AttachmentsSection,
    AttachmentUploadDialog,
    PreviewAttachmentModal,
} from './attachments.parts'
import {
    auditActionLabel,
    HIDDEN_AUDIT_ACTIONS,
    externalSystemLabel,
    processStatusLabel,
} from '@Purchasing/presentation/purchasing-labels'
import { downloadAttachmentFile, getAttachmentUrl } from '@/utils/firebaseStorage'

import { roles } from '@Purchasing/roles'
import { PurchaseProcessQuotes } from '@Purchasing/Quotes/PurchaseProcessQuotes'
import { formatBRL } from '@Purchasing/Utils/money'
import {
    AuditTimeline,
    detailCardClass,
    ExternalReferencesList,
    PurchaseSection,
    PurchaseStatusBadge,
} from './parts'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FilterSelect } from '@/components/filter-select'
import { StatusTag } from '@Purchasing/PurchaseProcessList/PurchaseProcessList.styles'
import { ProcessStatusControl } from './ProcessStatusControl'
import { EditableTitle } from './EditableTitle'
import { AssigneePicker } from '@Purchasing/ui/AssigneePicker'
import {
    NeedSource,
    PurchaseProcessStatus,
    SupplierQuoteStatus,
} from '@Purchasing/Domain/Model/PurchaseProcess'

// status humano via purchasing-labels (central)



export const PurchaseProcessDetails = () => {
    const { id } = useParams<{ id: string }>()
    const { process, loading, reload } = usePurchaseProcessById(id)
    const { events, loading: loadingAudit, reload: reloadAudit } =
        usePurchaseProcessAudit(id)
    const { stages, loading: loadingStages } = usePurchasingStages()
    const { quotes, reload: reloadQuotes } = useSupplierQuotes(id)
    const { purchase, loading: loadingPurchase, reload: reloadPurchase } =
        usePurchase(id)
    const attachments = useProcessAttachments(id)
    const {
        upload: uploadAttachment,
        remove: removeAttachment,
        uploading: attachmentUploading,
        progress: attachmentProgress,
        error: attachmentError,
    } = useAttachmentActions()
    const [uploadTarget, setUploadTarget] = useState<{
        entityType: string
        entityId: string
        suggestedCategory?: string
    } | null>(null)
    const [openingId, setOpeningId] = useState<string | null>(null)
    const [previewTarget, setPreviewTarget] =
        useState<PurchasingAttachment | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewError, setPreviewError] = useState(false)
    const [urlsByAttachment, setUrlsByAttachment] = useState<Record<string, string>>({})
    const [downloadingId, setDownloadingId] = useState<string | null>(null)
    const attachmentsData = attachments.data
    // §5 — thumbnails: URLs resolvidas em runtime apenas para imagens
    useEffect(() => {
        if (!attachmentsData) return
        const imageAtts = [
            ...attachmentsData.process,
            ...attachmentsData.quotes.flatMap((g) => g.attachments),
            ...attachmentsData.purchase,
            ...attachmentsData.payments.flatMap((g) => g.attachments),
        ].filter(
            (a) =>
                a.status === 'AVAILABLE' &&
                /^image\//.test(a.mimeType)
        )
        let cancelled = false
        Promise.allSettled(
            imageAtts.map(async (a) => {
                const url = await getAttachmentUrl(a.storagePath)
                if (!cancelled)
                    setUrlsByAttachment((prev) => ({ ...prev, [a._id]: url }))
            })
        )
        return () => {
            cancelled = true
        }
    }, [attachmentsData])
    const finance = usePurchaseFinance(id)
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const production = useProductionLinks(
        id,
        !permissionsLoading && hasPermission(roles.production.permissions.read.key)
    )
    const {
        create: createPayment,
        markPaid,
        cancel: cancelPayment,
        loading: paymentActionLoading,
        error: paymentActionError,
    } = usePurchasePaymentActions()
    const [newPaymentOpen, setNewPaymentOpen] = useState(false)
    const [payTarget, setPayTarget] = useState<PurchasePayment | null>(null)
    const {
        generate,
        changeStatus,
        save,
        loading: purchaseActionLoading,
        error: purchaseActionError,
    } = usePurchaseActions()
    const { changeStage, changeStatus: changeProcessStatus, changeAssignee, updateTitle, loading: processActionLoading, error: processActionError } = usePurchaseProcessManager()
    const [savingAssignee, setSavingAssignee] = useState(false)
    const [selectedStage, setSelectedStage] = useState('')

    if (loading && !process) return <Loader />

    if (!process) {
        return (
            <Container>
                <BackButton />
                <p className="text-od-secondary-600">
                    Processo não encontrado ou sem permissão para visualizar.
                </p>
            </Container>
        )
    }

    const need = process.need ?? {}
    const references = process.externalReferences ?? []
    const sourceItems = need.sourceItems ?? []

    const activeQuotes = quotes.filter((q) => q.active !== false)
    const winner =
        activeQuotes.find(
            (q) => q._id === process.selectedSupplierQuoteId
        ) ?? null

    const refreshAfterPurchase = async () => {
        await Promise.all([reloadPurchase(), finance.reload(), reload(), reloadAudit()])
    }

    const handleGeneratePurchase = async () => {
        const result = await generate(process._id)
        if (result) await refreshAfterPurchase()
    }

    const handlePurchaseStatus = async (status: SupplierQuoteStatus | any) => {
        const result = await changeStatus(process._id, status)
        if (result) await refreshAfterPurchase()
    }

    const handleSaveNotes = async (notes: string) => {
        const result = await save(process._id, { notes })
        if (result) await refreshAfterPurchase()
    }

    const refreshAfterPayment = async () => {
        await Promise.all([finance.reload(), reload(), reloadAudit()])
    }

    const handleCreatePayment = async (payload: any) => {
        const result = await createPayment(process._id, payload)
        if (result) {
            setNewPaymentOpen(false)
            await refreshAfterPayment()
        }
    }

    const handleConfirmPay = async (payload: any) => {
        if (!payTarget) return
        const result = await markPaid(process._id, payTarget._id, payload)
        if (result) {
            setPayTarget(null)
            await refreshAfterPayment()
        }
    }

    const handleCancelPayment = async (payment: PurchasePayment) => {
        const result = await cancelPayment(process._id, payment._id)
        if (result) await refreshAfterPayment()
    }

    const handleUploadConfirm = async (payload: any) => {
        if (!uploadTarget) return
        const result = await uploadAttachment(process._id, {
            entityType: uploadTarget.entityType as any,
            entityId: uploadTarget.entityId || process._id,
            category: payload.category,
            description: payload.description,
            originalFileName: payload.file.name,
            mimeType: payload.file.type,
            sizeBytes: payload.file.size,
            file: payload.file,
        })
        if (result) {
            setUploadTarget(null)
            await Promise.all([attachments.reload(), reloadAudit()])
        }
    }

    const resolvePreviewUrl = async (attachment: PurchasingAttachment) => {
        setPreviewLoading(true)
        setPreviewError(false)
        try {
            const url = await getAttachmentUrl(attachment.storagePath)
            setPreviewUrl(url)
        } catch {
            setPreviewUrl(null)
            setPreviewError(true)
        }
        setPreviewLoading(false)
    }

    const handlePreviewAttachment = (attachment: PurchasingAttachment) => {
        setPreviewTarget(attachment)
        setPreviewUrl(urlsByAttachment[attachment._id] ?? null)
        setPreviewError(false)
        setPreviewLoading(false)
        if (!urlsByAttachment[attachment._id]) resolvePreviewUrl(attachment)
    }

    const handleDownloadAttachment = async (
        attachment: PurchasingAttachment
    ) => {
        setDownloadingId(attachment._id)
        try {
            await downloadAttachmentFile(
                attachment.storagePath,
                attachment.originalFileName
            )
        } catch {
            // falha de download — feedback futuro via toast
        }
        setDownloadingId(null)
    }

    const handleRemoveAttachment = async (attachment: PurchasingAttachment) => {
        const result = await removeAttachment(process._id, attachment._id)
        if (result) {
            await Promise.all([attachments.reload(), reloadAudit()])
        }
    }

    const handleStageChange = async (value: string) => {
        if (!value || value === (selectedStage || process.stageId)) return
        const updated = await changeStage(process._id, value)
        if (updated) {
            setSelectedStage(value)
            await Promise.all([reload(), reloadAudit()])
        }
    }

    const handleTitleSave = async (title: string) => {
        const updated = await updateTitle(process._id, title)
        if (updated) await Promise.all([reload(), reloadAudit()])
        return !!updated
    }

    const handleAssigneeChange = async (id: string | null) => {
        setSavingAssignee(true)
        const updated = await changeAssignee(process._id, id)
        setSavingAssignee(false)
        if (updated) await Promise.all([reload(), reloadAudit()])
    }

    const handleProcessStatus = async (status: PurchaseProcessStatus) => {
        const updated = await changeProcessStatus(process._id, status)
        if (updated) await Promise.all([reload(), reloadAudit()])
    }

    return (
        <Container>
            <BackButton />

            {/* ---------- Header (§23) ---------- */}
            <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-brand-pink">
                        Processo de compra · {process.code}
                    </p>
                    <EditableTitle
                        title={process.title}
                        canEdit={!permissionsLoading && hasPermission(roles.process.permissions.update.key)}
                        saving={processActionLoading}
                        onSave={handleTitleSave}
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <StatusTag status={process.status} title="Situação do processo">
                            {processStatusLabel[process.status]}
                        </StatusTag>
                        {sourceItems.length > 0 && (
                            <span>
                                {sourceItems.length}{' '}
                                {sourceItems.length === 1 ? 'item do orçamento vinculado' : 'itens do orçamento vinculados'}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                            Responsável
                        </span>
                        <AssigneePicker
                            value={process.assignedTo}
                            onChange={handleAssigneeChange}
                            saving={savingAssignee}
                            disabled={permissionsLoading || !hasPermission(roles.process.permissions.update.key)}
                        />
                    </div>
                    <PermissionWrapper permission={roles.process.permissions.stage.key}>
                        <div className="flex flex-col gap-1">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                                Etapa no quadro
                            </span>
                            <FilterSelect
                                label="Etapa no quadro (coluna do Kanban)"
                                value={selectedStage || process.stageId || ''}
                                disabled={loadingStages || processActionLoading}
                                onChange={(value) => void handleStageChange(value)}
                                options={stages
                                    .filter((stage) => stage.active !== false || stage._id === process.stageId)
                                    .map((stage) => ({
                                        value: stage._id,
                                        label: `${stage.name}${stage.active === false ? ' (oculta)' : ''}`,
                                    }))}
                            />
                        </div>
                    </PermissionWrapper>
                    <PermissionWrapper permission={roles.process.permissions.status.key}>
                        <ProcessStatusControl status={process.status} busy={processActionLoading} onChange={handleProcessStatus} />
                    </PermissionWrapper>
                    {winner && !purchase && !loadingPurchase && (
                        <PermissionWrapper permission={roles.purchase.permissions.create.key}>
                            <Button
                                variant={ButtonVariant.primary}
                                onClick={handleGeneratePurchase}
                                disabled={purchaseActionLoading}
                                data-testid="header-generate-purchase"
                            >
                                {purchaseActionLoading ? 'Gerando…' : 'Gerar compra'}
                            </Button>
                        </PermissionWrapper>
                    )}
                </div>
            </div>
            {processActionError && <p role="alert" className="text-od-secondary-600 mb-3">{processActionError}</p>}

            {/* ---------- Hero summary (§24–§26) ---------- */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryCard title="Origem">
                    <KV
                        k="Fonte"
                        v={
                            process.need?.source === NeedSource.MANUAL
                                ? process.sourceSnapshot?.budget
                                    ? 'Manual · Brinde.me'
                                    : 'Manual'
                                : externalSystemLabel(process.sourceSnapshot?.source)
                        }
                    />
                    <KV
                        k="Orçamento"
                        v={process.sourceSnapshot?.budget?.code != null ? `#${process.sourceSnapshot.budget.code}` : 'Sem orçamento'}
                    />
                    <KV
                        k="Cliente"
                        v={
                            process.sourceSnapshot?.budget?.customer?.name ??
                            null
                        }
                    />
                    <KV
                        k="Empresa"
                        v={
                            process.sourceSnapshot?.budget?.company?.name ??
                            null
                        }
                    />
                    {sourceItems.length > 0 && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {sourceItems.length}{' '}
                            {sourceItems.length === 1
                                ? 'item vinculado'
                                : 'itens vinculados'}
                        </p>
                    )}
                </SummaryCard>

                <SummaryCard title="Necessidade">
                    <p className="text-sm text-gray-700 dark:text-gray-200 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden">
                        {need.description || '—'}
                    </p>
                    <KV k="Quantidade" v={`${need.quantity ?? '-'} un.`} />
                    {sourceItems.length > 1 && (
                        <details className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <summary className="cursor-pointer select-none">
                                Ver {sourceItems.length} linhas do orçamento
                            </summary>
                            <ul className="mt-1 space-y-0.5">
                                {sourceItems.map((si) => (
                                    <li key={si.sourceItemExternalId}>
                                        {si.description}
                                        {si.sku ? ` (${si.sku})` : ''} —{' '}
                                        {si.quantity} un.
                                        {si.matchStatus === 'MISSING' && (
                                            <span className="ml-1 font-semibold text-od-secondary-500">
                                                (missing)
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </SummaryCard>

                <SummaryCard title="Cotação vencedora">
                    {winner ? (
                        <>
                            <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                                {winner.supplierSnapshot?.corporateName ??
                                    winner.supplierSnapshot?.supplierId}
                            </p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {formatBRL(winner.totalCents)}
                            </p>
                            <KV k="Pagamento" v={winner.paymentTerms ?? null} />
                            <KV
                                k="Prazo"
                                v={
                                    winner.leadTimeDays
                                        ? `${winner.leadTimeDays} dias`
                                        : null
                                }
                            />
                        </>
                    ) : (
                        <EmptyHint text="Nenhuma cotação selecionada ainda." />
                    )}
                </SummaryCard>

                <SummaryCard title="Compra">
                    {purchase ? (
                        <>
                            <PurchaseStatusBadge status={purchase.status} />
                            <KV
                                k="Fornecedor"
                                v={
                                    purchase.supplierSnapshot?.corporateName ??
                                    null
                                }
                            />
                            <KV k="Total" v={formatBRL(purchase.totalCents)} />
                            {finance.summary && (
                                <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="hero-paid-line">
                                    Pago{" "}
                                    <strong className="text-gray-800 dark:text-gray-200">
                                        {formatBRL(finance.summary.paidCents)}
                                    </strong>{" "}
                                    de {formatBRL(finance.summary.totalCents)}
                                </p>
                            )}
                            {process.purchaseId && (
                                <button
                                    type="button"
                                    className="mt-1 text-xs underline text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                                    onClick={() =>
                                        document
                                            .getElementById('purchase-section-anchor')
                                            ?.scrollIntoView({
                                                behavior: 'smooth',
                                            })
                                    }
                                >
                                    Ver detalhes da compra
                                </button>
                            )}
                        </>
                    ) : (
                        <EmptyHint
                            text={
                                winner
                                    ? 'Pronta para gerar compra.'
                                    : 'Gere após escolher a vencedora.'
                            }
                        />
                    )}
                </SummaryCard>
            </div>

            {/* ---------- Cotações ---------- */}
            <PurchaseProcessQuotes
                processId={process._id}
                selectedSupplierQuoteId={process.selectedSupplierQuoteId}
                suggestedQuantity={need.quantity}
                reloadProcess={reload}
                onQuotesReload={() => {
                    reloadQuotes()
                    reloadAudit()
                }}
                closed={[PurchaseProcessStatus.FINISHED, PurchaseProcessStatus.LOST, PurchaseProcessStatus.CANCELED].includes(process.status)}
                winnerLocked={Boolean(purchase && purchase.status !== 'CANCELED')}
            />

            {/* ---------- Compra (Fase 3) ---------- */}
            <div id="purchase-section-anchor" />
            <PurchaseSection
                processId={process._id}
                purchase={purchase}
                loading={loadingPurchase}
                hasWinner={Boolean(winner)}
                onGenerate={handleGeneratePurchase}
                onChangeStatus={handlePurchaseStatus}
                onSaveNotes={handleSaveNotes}
                actionLoading={purchaseActionLoading}
                actionError={purchaseActionError}
            />

            <PermissionWrapper permission={roles.production.permissions.read.key}>
            <ProductionSection
                data={production.data}
                loading={production.loading}
                error={production.error}
                sourceItems={sourceItems}
                onConfirm={async (productionId) => {
                    await production.confirmLink(productionId)
                    await reloadAudit()
                }}
                onUnlink={async (productionId) => {
                    if (!window.confirm('Desvincular esta produção do processo?')) return
                    await production.unlinkLink(productionId)
                    await reloadAudit()
                }}
                onRefresh={async () => {
                    await production.refreshFromSource()
                    await reloadAudit()
                }}
            />
            </PermissionWrapper>

            {/* ---------- Financeiro (Fase 4) ---------- */}
{purchase && (
    <section className={`${detailCardClass} mt-4`} data-testid="finance-section">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">Financeiro</h2>
            {finance.summary &&
                finance.summary.remainingToPlanCents > 0 &&
                purchase.status !== 'CANCELED' && (
                <PermissionWrapper permission={roles.payment.permissions.create.key}>
                    <Button variant={ButtonVariant.primary} onClick={() => setNewPaymentOpen(true)} data-testid="new-payment">
                        Novo pagamento
                    </Button>
                </PermissionWrapper>
            )}
        </div>

        {finance.loading ? (
            <Loader />
        ) : finance.summary ? (
            <>
                <FinanceSummaryCard summary={finance.summary} />
                <div className="mt-4">
                    <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Pagamentos</span>
                    <PaymentsTable
                        payments={finance.payments}
                        onPayClick={(p) => setPayTarget(p)}
                        onCancelClick={handleCancelPayment}
                    />
                </div>
                {paymentActionError && <p className="mt-2 text-sm text-od-secondary-600">{paymentActionError}</p>}
            </>
        ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum pagamento registrado ainda.</p>
        )}
    </section>
)}

{/* ---------- Documentos (Fase 5) ---------- */}
<section className={`${detailCardClass} mt-4`} data-testid="attachments-section">
    <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Documentos</h2>
        <PermissionWrapper permission={roles.attachment.permissions.create.key}>
            <Button variant={ButtonVariant.secondary} onClick={() => setUploadTarget({ entityType: 'PURCHASE_PROCESS', entityId: process._id })} data-testid="add-document-header">
                Adicionar documento
            </Button>
        </PermissionWrapper>
    </div>
    <AttachmentsSection
        data={attachments.data}
        loading={attachments.loading}
        urlsByAttachment={urlsByAttachment}
        openingId={downloadingId}
        onPreview={handlePreviewAttachment}
        onDownload={handleDownloadAttachment}
        onRemove={handleRemoveAttachment}
        onUploadFor={(target) => setUploadTarget({ ...target, entityId: target.entityId || process._id })}
    />
</section>

{/* ---------- Referências externas (compacto) ---------- */}
            <section className={`${detailCardClass} mt-4`}>
                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Referências externas
                </h2>
                <ExternalReferencesList references={references} />
            </section>

            {/* ---------- Histórico / timeline ---------- */}
            <section className={`${detailCardClass} mt-4 mb-2`}>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Histórico
                </h2>
                <AuditTimeline
                    events={events}
                    loading={loadingAudit}
                    actionLabels={auditActionLabel}
                    hiddenActions={HIDDEN_AUDIT_ACTIONS}
                />
            </section>
            <NewPaymentModal
                isOpen={newPaymentOpen}
                onClose={() => setNewPaymentOpen(false)}
                remainingToPlanCents={finance.summary?.remainingToPlanCents ?? 0}
                submitting={paymentActionLoading}
                error={newPaymentOpen ? paymentActionError : null}
                onSubmit={handleCreatePayment}
            />
            <MarkPaidModal
                isOpen={Boolean(payTarget)}
                onClose={() => setPayTarget(null)}
                payment={payTarget}
                submitting={paymentActionLoading}
                error={payTarget ? paymentActionError : null}
                onConfirm={handleConfirmPay}
            />
            <PreviewAttachmentModal
                isOpen={Boolean(previewTarget)}
                onClose={() => setPreviewTarget(null)}
                attachment={previewTarget}
                url={previewUrl}
                loadingUrl={previewLoading}
                urlError={previewError}
                onRetry={() => previewTarget && resolvePreviewUrl(previewTarget)}
                onDownload={() =>
                    previewTarget && handleDownloadAttachment(previewTarget)
                }
                downloading={Boolean(previewTarget && downloadingId === previewTarget._id)}
            />
            <AttachmentUploadDialog
                isOpen={Boolean(uploadTarget)}
                onClose={() => setUploadTarget(null)}
                entityType={(uploadTarget?.entityType ?? 'PURCHASE_PROCESS') as any}
                entityId={uploadTarget?.entityId ?? process._id}
                suggestedCategory={uploadTarget?.suggestedCategory as any}
                uploading={attachmentUploading}
                progress={attachmentProgress}
                error={attachmentError}
                onConfirm={handleUploadConfirm}
            />
        </Container>
    )
}

const SummaryCard = ({
    title,
    children,
}: {
    title: string
    children: React.ReactNode
}) => (
    <div className={detailCardClass}>
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
            {title}
        </h2>
        <div className="space-y-1">{children}</div>
    </div>
)

const KV = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <p className="text-xs text-gray-500 dark:text-gray-400">
        <span className="mr-1 font-medium text-gray-600 dark:text-gray-300">
            {k}:
        </span>
        <span className="text-gray-800 dark:text-gray-200">{v ?? '—'}</span>
    </p>
)

const EmptyHint = ({ text }: { text: string }) => (
    <p className="rounded-md border border-dashed border-gray-300 px-3 py-2 text-xs italic text-gray-500 dark:border-gray-600 dark:text-gray-400">
        {text}
    </p>
)

// StatusTag reexportado para compatibilidade de imports existentes\nexport { StatusTag }
